/**
 * Converter for JSON form data to CampaignReportData structure
 * Handles conversion from CustomReportForm JSON format to structured report data
 */

import { CampaignReportData, CampaignMeta, Kpis, Conversions, Revenue, Retention, Halo, TimeOfDay, Feedback, StrategicTakeaways } from '../types/campaignReport';
import { parseNumber, parsePercentage } from './reportFormatters';

/**
 * Convert JSON form data to CampaignReportData structure
 */
export function convertFormDataToCampaignReport(formData: any): CampaignReportData | null {
  if (!formData || typeof formData !== 'object') {
    return null;
  }

  try {
    // Parse meta
    const meta: CampaignMeta = {
      location: formData.location || '',
      duration: formData.duration ? `${formData.duration} ${formData.durationUnit || 'months'}` : '',
      product: formData.productName || '',
      price: formData.productPrice || '',
      productStructure: formData.productStructure 
        ? formData.productStructure.split('\n').filter((line: string) => line.trim())
        : [],
    };

    // Parse KPIs
    const kpis: Kpis = {
      footfall: {
        monthlyAverage: formData.footfallZoneMonthly ? parseNumber(formData.footfallZoneMonthly) : undefined,
        sixMonthTotal: formData.footfallZoneTotal ? parseNumber(formData.footfallZoneTotal) : 0,
      },
      screenImpressions: {
        monthlyAverage: formData.screenImpressionsMonthly ? parseNumber(formData.screenImpressionsMonthly) : undefined,
        sixMonthTotal: formData.screenImpressionsTotal ? parseNumber(formData.screenImpressionsTotal) : 0,
      },
      interactions: {
        monthlyAverage: formData.screenClicksMonthly ? parseNumber(formData.screenClicksMonthly) : undefined,
        sixMonthTotal: formData.screenClicksTotal ? parseNumber(formData.screenClicksTotal) : 0,
      },
      browsingSessions: {
        monthlyAverage: formData.browsingSessionsMonthly ? parseNumber(formData.browsingSessionsMonthly) : undefined,
        sixMonthTotal: formData.browsingSessionsTotal ? parseNumber(formData.browsingSessionsTotal) : 0,
      },
      averageBrowseTime: formData.averageBrowseTimeSeconds ? parseNumber(formData.averageBrowseTimeSeconds) : undefined,
      footfallToBrowseRate: formData.footfallToBrowseRate ? parsePercentage(formData.footfallToBrowseRate) : undefined,
      totalUnitsSold: formData.totalUnits ? parseNumber(formData.totalUnits) : 0,
      totalCampaignRevenue: formData.totalCampaignRevenue ? parseNumber(formData.totalCampaignRevenue) : 0,
    };

    // Add ad playouts if available
    if (formData.adPlayoutsRotating || formData.adPlayoutsTotalPlays) {
      kpis.adPlayouts = {
        description: formData.adPlayoutsRotating || '6 ads rotating',
        totalPlays: formData.adPlayoutsTotalPlays ? parseNumber(formData.adPlayoutsTotalPlays) : undefined,
      };
    }

    // Parse Conversions
    const conversions: Conversions = {
      browseToPurchaseRate: formData.conversionBrowsingToPurchase ? parsePercentage(formData.conversionBrowsingToPurchase) : 0,
      footfallToPurchaseRate: formData.conversionFootfallToPurchase ? parsePercentage(formData.conversionFootfallToPurchase) : 0,
      averageSellingPrice: formData.averageSellingPrice ? parseNumber(formData.averageSellingPrice) : 0,
      monthlyUnitsSold: formData.monthlyUnitsSold ? parseNumber(formData.monthlyUnitsSold) : undefined,
      totalUnitsSold: formData.totalUnits ? parseNumber(formData.totalUnits) : 0,
    };

    // Parse Revenue
    const revenue: Revenue = {
      initialBox: {
        monthly: formData.initialBoxRevenueMonthly ? parseNumber(formData.initialBoxRevenueMonthly) : undefined,
        sixMonthTotal: formData.initialBoxRevenueTotal ? parseNumber(formData.initialBoxRevenueTotal) : 0,
      },
      subscription: {
        monthly: formData.subscriptionRevenueMonthly ? parseNumber(formData.subscriptionRevenueMonthly) : undefined,
        sixMonthTotal: formData.subscriptionRevenueTotal ? parseNumber(formData.subscriptionRevenueTotal) : 0,
        description: formData.subscriptionRevenueDescription || 'blended',
      },
      totalCampaignRevenue: formData.totalCampaignRevenue ? parseNumber(formData.totalCampaignRevenue) : 0,
      totalTransactionValueProcessed: formData.totalTransactionValueProcessed ? parseNumber(formData.totalTransactionValueProcessed) : undefined,
    };

    // Parse Retention
    const retention: Retention = {
      threeMonthCompletionRate: formData.threeMonthCompletionRate ? parsePercentage(formData.threeMonthCompletionRate) : 0,
      churnRate: formData.churnRate ? parsePercentage(formData.churnRate) : 0,
      autoRenewTakeUp: formData.autoRenewTakeUp ? parsePercentage(formData.autoRenewTakeUp) : undefined,
      averageLifetimeValue: formData.averageLifetimeValue ? parseNumber(formData.averageLifetimeValue) : 0,
    };

    // Parse Halo
    const halo: Halo = {
      footfallUplift: formData.footfallUplift ? parsePercentage(formData.footfallUplift) : 0,
      giftCardRedemption: formData.giftCardRedemptionInStore ? parsePercentage(formData.giftCardRedemptionInStore) : undefined,
      sameDayCrossPurchase: formData.sameDayCrossPurchase ? parsePercentage(formData.sameDayCrossPurchase) : undefined,
      estimatedIncrementalStoreRevenue: formData.estimatedIncrementalStoreRevenue ? parseNumber(formData.estimatedIncrementalStoreRevenue) : 0,
    };

    // Parse Time of Day
    const timeOfDay: TimeOfDay = {
      sales10am1pm: formData.salesTime10am1pm ? parsePercentage(formData.salesTime10am1pm) : 0,
      sales1pm4pm: formData.salesTime1pm4pm ? parsePercentage(formData.salesTime1pm4pm) : 0,
      sales4pm7pm: formData.salesTime4pm7pm ? parsePercentage(formData.salesTime4pm7pm) : 0,
      sales7pm9pm: formData.salesTime7pm9pm ? parsePercentage(formData.salesTime7pm9pm) : 0,
      peakWindow: '4pm-7pm', // Default peak window
    };

    // Parse Feedback
    const feedback: Feedback = {
      overallExperienceScore: formData.overallExperienceScore ? parseNumber(formData.overallExperienceScore) : 0,
      easeOfPurchaseScore: formData.easeOfPurchaseScore ? parseNumber(formData.easeOfPurchaseScore) : undefined,
      giftAppealRating: formData.giftAppealRating ? parseNumber(formData.giftAppealRating) : undefined,
      wouldRecommend: formData.wouldRecommend ? parsePercentage(formData.wouldRecommend) : 0,
      commonThemes: formData.commonFeedbackThemes 
        ? (typeof formData.commonFeedbackThemes === 'string'
          ? formData.commonFeedbackThemes.split('\n').filter((line: string) => line.trim()).map((line: string) => line.replace(/^[-•]\s*/, '').trim())
          : Array.isArray(formData.commonFeedbackThemes) 
          ? formData.commonFeedbackThemes 
          : [])
        : [],
    };

    // Parse Strategic Takeaways
    let strategicTakeaways: StrategicTakeaways | undefined;
    if (formData.descriptionSections?.strategicTakeaways || formData.strategicTakeaways) {
      const takeawaysText = formData.descriptionSections?.strategicTakeaways || formData.strategicTakeaways;
      const points = typeof takeawaysText === 'string' 
        ? takeawaysText.split('\n').filter((line: string) => line.trim()).map((line: string) => line.replace(/^[-•]\s*/, '').trim())
        : Array.isArray(takeawaysText) 
        ? takeawaysText 
        : [];
      if (points.length > 0) {
        strategicTakeaways = { points };
      }
    }

    return {
      meta,
      kpis,
      conversions,
      revenue,
      retention,
      halo,
      timeOfDay,
      feedback,
      strategicTakeaways,
    };
  } catch (error) {
    console.error('Error converting form data to campaign report:', error);
    return null;
  }
}

/**
 * Check if form data contains campaign report fields
 */
export function isFormDataStructured(formData: any): boolean {
  if (!formData || typeof formData !== 'object') {
    return false;
  }

  // Check for key fields that indicate this is form data
  return !!(
    formData.footfallZoneTotal ||
    formData.screenImpressionsTotal ||
    formData.totalCampaignRevenue ||
    formData.conversionBrowsingToPurchase ||
    formData.location ||
    formData.productName
  );
}
